/**
 * Adds AI disclosure badges to every image whose alt text uses a supported
 * disclosure prefix. Structured theme media renders an inert marker server-side
 * via the `media-disclosure` Liquid snippet. This script replaces those markers
 * with valid sibling buttons and fills the gaps for editor-provided, hard-coded,
 * and dynamically inserted images.
 *
 * The historical filename and data attributes are retained for compatibility.
 */
(() => {
  const configurationElement =
    document.currentScript || document.querySelector('script[data-media-disclosure-rte]');

  if (!(configurationElement instanceof HTMLScriptElement)) return;

  // A second script/section evaluation must not install another observer.
  const initializationKey = Symbol.for('theme.mediaDisclosureRte');
  if (document[initializationKey]) return;
  document[initializationKey] = true;

  const labels = {
    compact: configurationElement.dataset.compact || 'AI',
  };

  const disclosurePrefix =
    /^\s*(ki(?:-|\s+)generiert|ki(?:-|\s+)bearbeitet|mit(?:-|\s+)ki(?:-|\s+)bearbeitet|ki(?:-|\s+)visualisierung|ai(?:-|\s+)generated|ai(?:-|\s+)modified|ai(?:-|\s+)visualization|ai)\s*:/i;
  const hostAttribute = 'data-media-disclosure-rte-host';
  const badgeAttribute = 'data-media-disclosure-rte-badge';
  const skipSelector = '.stories-section, [data-ai-disclosure-skip]';
  const interactiveSelector =
    'a[href], button, input, select, textarea, summary, [role="button"], [role="link"]';
  const serverBadgeSelector = `.media-disclosure[data-ai-state]:not([${badgeAttribute}])`;
  const disclosureAltRepairs = [
    {
      sourceFragment:
        '/articles/01_-_Das_bestes_Keratin-Shampoo_fuer_dein_Haar-_Glanz_Reparatur_Schutz_',
      alt:
        'KI-generiert: Frau mit langem, glattem, dunklem Haar neben einer goldfarbenen Haarpflegeflasche und einer grünen Pflanze',
    },
  ];
  const fallbackEntries = new WeakMap();
  const activeFallbackImages = new Set();
  const badgeImages = new WeakMap();
  const pendingImages = new Set();
  const pendingScopes = new Set();
  const dirtyPositions = new Set();
  let positionAll = false;
  let positionFrame = 0;
  let resizeObserver = null;
  let intersectionObserver = null;
  let disclosurePopover = null;
  let activeDisclosureBadge = null;

  /**
   * @param {string} altText
   * @returns {{ state: 'generated' | 'modified' | 'visualization' | 'generic', descriptionMissing: boolean, detail: string } | null}
   */
  function parseDisclosure(altText) {
    const match = altText.match(disclosurePrefix);
    if (!match) return null;

    const normalizedPrefix = match[1].toLowerCase().replace(/[\s-]+/g, ' ');
    let state;

    if (normalizedPrefix === 'ki generiert' || normalizedPrefix === 'ai generated') {
      state = 'generated';
    } else if (
      normalizedPrefix === 'ki bearbeitet' ||
      normalizedPrefix === 'mit ki bearbeitet' ||
      normalizedPrefix === 'ai modified'
    ) {
      state = 'modified';
    } else if (normalizedPrefix === 'ai') {
      state = 'generic';
    } else {
      state = 'visualization';
    }

    const detail = altText.trim();

    return {
      state,
      descriptionMissing: altText.slice(match[0].length).trim() === '',
      detail,
    };
  }

  /**
   * Repairs a known incomplete Shopify article alt while preserving Shopify as
   * the source of truth for every complete description. Once the stored alt is
   * corrected, this exact fallback becomes a no-op.
   *
   * @param {HTMLImageElement} image
   * @returns {string}
   */
  function resolveDisclosureAlt(image) {
    const altText = image.getAttribute('alt') || '';
    const disclosure = parseDisclosure(altText);
    if (!disclosure?.descriptionMissing) return altText;

    const source = image.currentSrc || image.src || '';
    const repair = disclosureAltRepairs.find(({ sourceFragment }) => source.includes(sourceFragment));
    if (!repair) return altText;

    image.setAttribute('alt', repair.alt);
    return repair.alt;
  }

  /**
   * @param {Element | null} node
   * @returns {HTMLElement | null}
   */
  function getServerRenderedDisclosure(node) {
    if (!(node instanceof HTMLElement)) return null;

    if (node.matches(serverBadgeSelector)) return node;
    const disclosure = node.querySelector(serverBadgeSelector);
    return disclosure instanceof HTMLElement ? disclosure : null;
  }

  /**
   * Prevents the fallback from duplicating a badge rendered by Liquid. Theme
   * renderers place the disclosure immediately after the image, picture, link,
   * or local media container. A disclosure can itself be nested in a wrapper
   * (notably `resource-card__disclosure`), so the sibling is inspected as a
   * subtree. Traversal only continues through single-media wrappers to avoid
   * borrowing a badge from a neighboring gallery or carousel image.
   *
   * @param {HTMLImageElement} image
   * @returns {HTMLElement | null}
   */
  function findServerRenderedDisclosure(image) {
    let media = image.parentElement?.tagName === 'PICTURE' ? image.parentElement : image;

    while (media instanceof HTMLElement && media !== document.body) {
      const nextSibling = media.nextElementSibling;
      const siblingDisclosure = getServerRenderedDisclosure(nextSibling);

      if (
        siblingDisclosure &&
        !nextSibling?.matches('img') &&
        nextSibling?.querySelector('img') === null
      ) {
        return siblingDisclosure;
      }

      const parent = media.parentElement;
      if (!(parent instanceof HTMLElement)) return null;

      const parentImages = parent.querySelectorAll('img');
      if (parentImages.length !== 1 || parentImages[0] !== image) return null;
      const parentDisclosure = getServerRenderedDisclosure(parent);
      if (parentDisclosure) return parentDisclosure;

      const meaningfulChildren = Array.from(parent.children).filter(
        (child) => !(child instanceof HTMLElement && child.hasAttribute(badgeAttribute))
      );

      if (meaningfulChildren.length !== 1 || meaningfulChildren[0] !== media) return null;
      media = parent;
    }

    return null;
  }

  /**
   * Selects an already-positioned, non-interactive ancestor that can carry an
   * absolutely positioned button. Skipping links and controls keeps the final
   * DOM valid even when Liquid's inert marker was rendered inside one. Falling
   * back to `body` avoids rebasing existing component overlays.
   *
   * @param {HTMLElement} media
   * @returns {HTMLElement}
   */
  function selectOverlayHost(media, measurements) {
    let candidate = media.parentElement;

    while (candidate instanceof HTMLElement && candidate !== document.body) {
      const candidateStyle = measurements.style(candidate);
      const display = candidateStyle.display;
      const isInsideInteractiveControl = candidate.closest(interactiveSelector) !== null;

      if (
        !isInsideInteractiveControl &&
        candidateStyle.position !== 'static' &&
        display !== 'inline' &&
        display !== 'contents' &&
        display !== 'table-row' &&
        display !== 'table-row-group' &&
        display !== 'table-column' &&
        display !== 'table-column-group'
      ) {
        return candidate;
      }

      candidate = candidate.parentElement;
    }

    return document.body;
  }

  /**
   * @param {HTMLImageElement} image
   * @returns {{ host: HTMLElement, badge: HTMLButtonElement, media: HTMLElement, usesSlot: boolean }}
   */
  function createFallbackEntry(image, preparedHost) {
    const media = image.parentElement?.tagName === 'PICTURE' ? image.parentElement : image;
    const disclosureSlot = media.nextElementSibling;
    const usesSlot =
      disclosureSlot instanceof HTMLElement &&
      disclosureSlot.matches('.resource-card__disclosure') &&
      disclosureSlot.querySelector('img') === null &&
      disclosureSlot.closest(interactiveSelector) === null;
    const host = usesSlot ? disclosureSlot : preparedHost;

    if (!host.classList.contains('media-disclosure-rte__host')) {
      host.classList.add('media-disclosure-rte__host');
    }
    host.setAttribute(hostAttribute, '');

    const badge = document.createElement('button');
    badge.type = 'button';
    badge.className = 'media-disclosure';
    badge.setAttribute(badgeAttribute, '');

    if (!usesSlot) {
      badge.classList.add('media-disclosure-rte__badge--positioned');
      badge.style.visibility = 'hidden';
      // Clear the previous bottom-right defaults so positioning remains correct
      // even while an older base.css response is present in a browser or cache.
      badge.style.insetBlockEnd = 'auto';
      badge.style.insetInlineEnd = 'auto';
      badge.style.right = 'auto';
      badge.style.bottom = 'auto';
    }
    host.append(badge);

    const entry = { host, badge, media, usesSlot };
    fallbackEntries.set(image, entry);
    badgeImages.set(badge, image);
    activeFallbackImages.add(image);
    // A slot badge can participate in normal flow and shift other images.
    positionAll = true;
    resizeObserver?.observe(image);
    intersectionObserver?.observe(image);
    return entry;
  }

  /**
   * @param {HTMLImageElement} image
   * @returns {{ host: HTMLElement, badge: HTMLButtonElement, media: HTMLElement, usesSlot: boolean } | null}
   */
  function getFallbackEntry(image) {
    const entry = fallbackEntries.get(image);

    if (entry?.badge?.isConnected && entry.host?.isConnected) return entry;
    fallbackEntries.delete(image);
    activeFallbackImages.delete(image);
    resizeObserver?.unobserve(image);
    intersectionObserver?.unobserve(image);

    if (entry?.host?.isConnected) {
      const hasOwnFallbackBadge = Array.from(entry.host.children).some(
        (child) => child instanceof HTMLElement && child.hasAttribute(badgeAttribute)
      );

      if (!hasOwnFallbackBadge) {
        entry.host.classList.remove('media-disclosure-rte__host');
        entry.host.classList.remove('media-disclosure-rte__host--positioned');
        entry.host.removeAttribute(hostAttribute);
      }
    }

    return null;
  }

  /**
   * @param {HTMLImageElement} image
   * @param {{ host: HTMLElement, badge: HTMLButtonElement, media: HTMLElement, usesSlot: boolean }} entry
   */
  function removeFallbackEntry(image, entry) {
    fallbackEntries.delete(image);
    activeFallbackImages.delete(image);
    resizeObserver?.unobserve(image);
    intersectionObserver?.unobserve(image);
    if (activeDisclosureBadge === entry.badge) closeDisclosurePopover();
    entry.badge.remove();
    positionAll = true;

    const hasOwnFallbackBadge = Array.from(entry.host.children).some(
      (child) => child instanceof HTMLElement && child.hasAttribute(badgeAttribute)
    );

    if (!hasOwnFallbackBadge) {
      entry.host.classList.remove('media-disclosure-rte__host');
      entry.host.classList.remove('media-disclosure-rte__host--positioned');
      entry.host.removeAttribute(hostAttribute);
    }
  }

  /**
   * @param {HTMLButtonElement} badge
   * @param {{ state: 'generated' | 'modified' | 'visualization' | 'generic', descriptionMissing: boolean, detail: string }} disclosure
   */
  function updateBadge(badge, disclosure) {
    badge.dataset.aiState = disclosure.state;
    badge.dataset.aiDisclosureDetail = disclosure.detail;
    badge.classList.add('media-disclosure--compact');
    badge.setAttribute('aria-label', disclosure.detail);
    badge.setAttribute('aria-expanded', activeDisclosureBadge === badge ? 'true' : 'false');
    badge.setAttribute('aria-controls', 'media-disclosure-popover');
    badge.type = 'button';
    if (badge.textContent !== labels.compact) badge.textContent = labels.compact;

    if (disclosure.descriptionMissing) {
      badge.dataset.aiDescriptionMissing = 'true';
    } else {
      delete badge.dataset.aiDescriptionMissing;
    }

    if (activeDisclosureBadge === badge && disclosurePopover && !disclosurePopover.hidden) {
      if (disclosurePopover.textContent !== disclosure.detail) {
        disclosurePopover.textContent = disclosure.detail;
      }
      scheduleFallbackPositions();
    }
  }

  function ensureDisclosurePopover() {
    if (disclosurePopover?.isConnected) return disclosurePopover;

    disclosurePopover = document.createElement('div');
    disclosurePopover.id = 'media-disclosure-popover';
    disclosurePopover.className = 'media-disclosure-popover';
    disclosurePopover.setAttribute('role', 'tooltip');
    disclosurePopover.setAttribute('aria-live', 'polite');
    disclosurePopover.hidden = true;
    document.body.append(disclosurePopover);
    return disclosurePopover;
  }

  function closeDisclosurePopover({ restoreFocus = false } = {}) {
    const previousBadge = activeDisclosureBadge;
    activeDisclosureBadge = null;

    if (previousBadge?.isConnected) {
      previousBadge.setAttribute('aria-expanded', 'false');
      previousBadge.removeAttribute('aria-describedby');
    }
    if (disclosurePopover) disclosurePopover.hidden = true;
    if (restoreFocus && previousBadge?.isConnected) previousBadge.focus({ preventScroll: true });
  }

  function positionDisclosurePopover() {
    if (!activeDisclosureBadge?.isConnected || !disclosurePopover || disclosurePopover.hidden) return;

    const triggerRect = activeDisclosureBadge.getBoundingClientRect();
    const triggerStyle = window.getComputedStyle(activeDisclosureBadge);
    if (
      triggerRect.width <= 0 ||
      triggerRect.height <= 0 ||
      triggerRect.bottom < 0 ||
      triggerRect.top > window.innerHeight ||
      triggerStyle.display === 'none' ||
      triggerStyle.visibility === 'hidden' ||
      Number.parseFloat(triggerStyle.opacity) <= 0.001
    ) {
      closeDisclosurePopover();
      return;
    }

    const viewportMargin = 8;
    const triggerGap = 8;
    const popoverRect = disclosurePopover.getBoundingClientRect();
    const maximumLeft = Math.max(viewportMargin, window.innerWidth - popoverRect.width - viewportMargin);
    const left = Math.min(Math.max(viewportMargin, triggerRect.left), maximumLeft);
    let top = triggerRect.bottom + triggerGap;

    if (top + popoverRect.height > window.innerHeight - viewportMargin) {
      top = triggerRect.top - popoverRect.height - triggerGap;
    }

    const maximumTop = Math.max(viewportMargin, window.innerHeight - popoverRect.height - viewportMargin);
    top = Math.min(Math.max(viewportMargin, top), maximumTop);
    const nextLeft = `${left}px`;
    const nextTop = `${top}px`;
    if (disclosurePopover.style.left !== nextLeft) disclosurePopover.style.left = nextLeft;
    if (disclosurePopover.style.top !== nextTop) disclosurePopover.style.top = nextTop;
  }

  function toggleDisclosurePopover(badge) {
    if (activeDisclosureBadge === badge && disclosurePopover && !disclosurePopover.hidden) {
      closeDisclosurePopover();
      return;
    }

    if (activeDisclosureBadge?.isConnected) {
      activeDisclosureBadge.setAttribute('aria-expanded', 'false');
      activeDisclosureBadge.removeAttribute('aria-describedby');
    }

    const popover = ensureDisclosurePopover();
    activeDisclosureBadge = badge;
    badge.setAttribute('aria-expanded', 'true');
    badge.setAttribute('aria-describedby', popover.id);
    popover.textContent = badge.dataset.aiDisclosureDetail || badge.getAttribute('aria-label') || 'AI';
    popover.hidden = false;
    positionDisclosurePopover();
  }

  function handleDisclosureClick(event) {
    const target = event.target;
    const badge = target instanceof Element ? target.closest('.media-disclosure') : null;

    if (badge instanceof HTMLElement && badge.dataset.aiDisclosureDetail) {
      event.preventDefault();
      event.stopPropagation();
      toggleDisclosurePopover(badge);
      return;
    }

    if (target instanceof Node && disclosurePopover?.contains(target)) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (activeDisclosureBadge) closeDisclosurePopover();
  }

  function handleDisclosureKeydown(event) {
    if (event.key === 'Escape' && activeDisclosureBadge) {
      event.preventDefault();
      closeDisclosurePopover({ restoreFocus: true });
      return;
    }
  }

  /**
   * Tracks visual state between an image and its overlay host. This matters
   * when carousels keep several slides in the DOM and hide inactive ones with
   * opacity or visibility instead of removing them.
   *
   * @param {HTMLImageElement} image
   * @param {HTMLElement} host
   * @returns {{ visible: boolean, opacity: number }}
   */
  function getImageVisualState(image, host, measurements) {
    let current = image;
    let opacity = 1;

    while (current instanceof HTMLElement && current !== host) {
      const style = measurements.style(current);

      if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') {
        return { visible: false, opacity: 0 };
      }

      const currentOpacity = Number.parseFloat(style.opacity);
      if (Number.isFinite(currentOpacity)) opacity *= currentOpacity;
      if (opacity <= 0.001) return { visible: false, opacity: 0 };
      current = current.parentElement;
    }

    return { visible: true, opacity };
  }

  /**
   * @param {HTMLImageElement} image
   * @param {DOMRect} mediaRect
   * @param {HTMLElement} host
   * @returns {{ left: number, right: number, top: number, bottom: number }}
   */
  function getVisibleMediaBounds(image, mediaRect, host, measurements) {
    const bounds = {
      left: mediaRect.left,
      right: mediaRect.right,
      top: mediaRect.top,
      bottom: mediaRect.bottom,
    };
    let current = image.parentElement;

    while (current instanceof HTMLElement) {
      const style = measurements.style(current);
      const rect = style.overflowX !== 'visible' || style.overflowY !== 'visible'
        ? measurements.rect(current)
        : null;

      if (style.overflowX !== 'visible') {
        bounds.left = Math.max(bounds.left, rect.left);
        bounds.right = Math.min(bounds.right, rect.right);
      }

      if (style.overflowY !== 'visible') {
        bounds.top = Math.max(bounds.top, rect.top);
        bounds.bottom = Math.min(bounds.bottom, rect.bottom);
      }

      if (current === host) break;
      current = current.parentElement;
    }

    return bounds;
  }

  /**
   * Reads the next fallback badge position without changing the media DOM.
   * Coordinates are expressed in the selected host's untransformed CSS pixels.
   *
   * @param {HTMLImageElement} image
   * @param {{ host: HTMLElement, badge: HTMLButtonElement, media: HTMLElement, usesSlot: boolean }} entry
   */
  function positionFallbackEntry(image, entry, measurements) {
    if (entry.usesSlot || !image.isConnected || !entry.badge.isConnected) return;

    const mediaRect = measurements.rect(entry.media);
    const hostRect = measurements.rect(entry.host);
    const hostMetrics = measurements.host(entry.host);
    const hostWidth = hostMetrics.offsetWidth || hostRect.width;
    const hostHeight = hostMetrics.offsetHeight || hostRect.height;
    const scaleX = hostWidth > 0 && hostRect.width > 0 ? hostRect.width / hostWidth : 1;
    const scaleY = hostHeight > 0 && hostRect.height > 0 ? hostRect.height / hostHeight : 1;
    const visualState = getImageVisualState(image, entry.host, measurements);
    const visibleBounds = getVisibleMediaBounds(image, mediaRect, entry.host, measurements);
    const visibleLeft = visibleBounds.left;
    const visibleRight = visibleBounds.right;
    const visibleTop = visibleBounds.top;
    const visibleBottom = visibleBounds.bottom;
    const visibleWidth = visibleRight - visibleLeft;
    const visibleHeight = visibleBottom - visibleTop;
    const visible = visualState.visible && visibleWidth > 0 && visibleHeight > 0;

    const styles = {
      visibility: visible ? '' : 'hidden',
      opacity: visualState.opacity < 0.999 ? String(visualState.opacity) : '',
    };
    if (!visible) return { badge: entry.badge, styles };

    const availableWidth = Math.max(28, visibleWidth / scaleX - 16);
    styles.maxWidth = `${availableWidth}px`;

    const originLeft = hostRect.left + hostMetrics.clientLeft * scaleX;
    const originTop = hostRect.top + hostMetrics.clientTop * scaleY;
    const mediaLeft = (visibleLeft - originLeft) / scaleX + hostMetrics.scrollLeft;
    const mediaTop = (visibleTop - originTop) / scaleY + hostMetrics.scrollTop;
    const left = Math.max(0, mediaLeft + 8);
    const top = Math.max(0, mediaTop + 8);

    styles.left = `${left}px`;
    styles.top = `${top}px`;
    return { badge: entry.badge, styles };
  }

  // Measurements are deliberately scoped to one read phase. Computed styles
  // and coordinates must never survive DOM writes, scrolling, or another frame.
  function createMeasurements() {
    const styles = new Map();
    const rects = new Map();
    const hosts = new Map();
    return {
      style(node) {
        if (!styles.has(node)) styles.set(node, window.getComputedStyle(node));
        return styles.get(node);
      },
      rect(node) {
        if (!rects.has(node)) rects.set(node, node.getBoundingClientRect());
        return rects.get(node);
      },
      host(node) {
        if (!hosts.has(node)) hosts.set(node, {
          offsetWidth: node.offsetWidth, offsetHeight: node.offsetHeight,
          clientLeft: node.clientLeft, clientTop: node.clientTop,
          scrollLeft: node.scrollLeft, scrollTop: node.scrollTop,
        });
        return hosts.get(node);
      },
    };
  }

  function queueImage(image) {
    pendingImages.add(image);
    dirtyPositions.add(image);
    requestPositionFrame();
  }

  function scheduleFallbackPositions() {
    positionAll = true;
    requestPositionFrame();
  }

  function requestPositionFrame() {
    if (positionFrame) return;
    positionFrame = window.requestAnimationFrame(() => {
      positionFrame = 0;
      // Deduplicate overlapping inserted/invalidated scopes before querying.
      const scopes = Array.from(pendingScopes);
      pendingScopes.clear();
      scopes.forEach((scope) => {
        if (!scope.isConnected || scopes.some((other) => other !== scope && other.contains(scope))) return;
        if (scope instanceof HTMLImageElement) pendingImages.add(scope);
        scope.querySelectorAll('img[alt]').forEach((image) => pendingImages.add(image));
      });
      const images = Array.from(pendingImages).filter((image) => image.isConnected);
      pendingImages.clear();
      const hostMeasurements = createMeasurements();
      // Select all new hosts before creating any badges (style reads first).
      const preparedHosts = new Map();
      images.forEach((image) => {
        if (!fallbackEntries.get(image)?.badge.isConnected &&
          !image.closest(skipSelector) && parseDisclosure(image.getAttribute('alt') || '')) {
          const media = image.parentElement?.tagName === 'PICTURE' ? image.parentElement : image;
          preparedHosts.set(image, selectOverlayHost(media, hostMeasurements));
        }
      });
      images.forEach((image) => {
        synchronizeImage(image, preparedHosts.get(image));
        dirtyPositions.add(image);
      });

      const positionedImages = positionAll ? Array.from(activeFallbackImages) : Array.from(dirtyPositions);
      positionAll = false;
      dirtyPositions.clear();
      const entries = [];
      // Removal/repair is also completed before the geometry read phase.
      positionedImages.forEach((image) => {
        const entry = getFallbackEntry(image);
        if (!image.isConnected) {
          if (entry) removeFallbackEntry(image, entry);
        } else if (entry) entries.push([image, entry]);
      });
      const measurements = createMeasurements();
      const positions = entries.map(([image, entry]) => positionFallbackEntry(image, entry, measurements));
      // No layout/style reads between the first and last badge write.
      positions.forEach((position) => {
        if (!position) return;
        Object.entries(position.styles).forEach(([property, value]) => {
          if (position.badge.style[property] !== value) position.badge.style[property] = value;
        });
      });
      // The open popover follows the newly written trigger position.
      positionDisclosurePopover();
    });
  }

  /**
   * @param {HTMLImageElement} image
   */
  function synchronizeImage(image, preparedHost) {
    const disclosure = image.closest(skipSelector)
      ? null
      : parseDisclosure(resolveDisclosureAlt(image));
    const serverBadge = findServerRenderedDisclosure(image);
    let fallbackEntry = getFallbackEntry(image);

    if (serverBadge) {
      if (!disclosure) {
        if (activeDisclosureBadge === serverBadge) closeDisclosurePopover();
        serverBadge.remove();
        if (fallbackEntry) removeFallbackEntry(image, fallbackEntry);
        return;
      }

      if (!fallbackEntry) fallbackEntry = createFallbackEntry(image, preparedHost);
      updateBadge(fallbackEntry.badge, disclosure);
      serverBadge.remove();
      return;
    }

    if (!disclosure) {
      if (fallbackEntry) removeFallbackEntry(image, fallbackEntry);
      return;
    }

    if (!fallbackEntry) fallbackEntry = createFallbackEntry(image, preparedHost);

    updateBadge(fallbackEntry.badge, disclosure);
  }

  /**
   * @param {Node} node
   */
  function processNode(node) {
    if (!(node instanceof Element)) return;

    pendingScopes.add(node);
    requestPositionFrame();
  }

  /**
   * @param {Node} node
   */
  function cleanupNode(node) {
    if (!(node instanceof Element)) return;

    const images = node instanceof HTMLImageElement ? [node] : Array.from(node.querySelectorAll('img'));

    images.forEach((image) => {
      if (!(image instanceof HTMLImageElement)) return;
      const entry = getFallbackEntry(image);
      if (entry) removeFallbackEntry(image, entry);
    });
  }

  /**
   * Rechecks the nearest DOM scope containing images after a server-rendered
   * disclosure is added or removed dynamically.
   *
   * @param {Node} node
   */
  function processNearestImageScope(node) {
    let scope = node instanceof Element ? node : node.parentElement;

    while (scope instanceof Element && scope !== document.body) {
      if (scope instanceof HTMLImageElement || scope.querySelector('img')) {
        if (scope instanceof HTMLImageElement) queueImage(scope);
        else processNode(scope);
        return;
      }

      scope = scope.parentElement;
    }
  }

  /**
   * @param {Node} node
   * @returns {boolean}
   */
  function containsServerDisclosureNode(node) {
    return node instanceof Element && getServerRenderedDisclosure(node) !== null;
  }

  function initialize() {
    ensureDisclosurePopover();

    if (typeof ResizeObserver === 'function') {
      resizeObserver = new ResizeObserver(scheduleFallbackPositions);
    }

    if (typeof IntersectionObserver === 'function') {
      intersectionObserver = new IntersectionObserver(scheduleFallbackPositions);
    }

    processNode(document.body);

    const observer = new MutationObserver((mutations) => {
      let shouldReposition = false;

      mutations.forEach((mutation) => {
        if (
          disclosurePopover &&
          (mutation.target === disclosurePopover || disclosurePopover.contains(mutation.target))
        ) {
          return;
        }

        if (mutation.target instanceof Element && mutation.target.closest(`[${badgeAttribute}]`)) return;

        if (mutation.type === 'attributes' && mutation.target instanceof HTMLImageElement) {
          if (
            mutation.attributeName === 'alt' ||
            mutation.attributeName === 'data-ai-disclosure-compact' ||
            mutation.attributeName === 'data-ai-disclosure-skip'
          ) {
            queueImage(mutation.target);
          }

          // Keep metadata updates local. ResizeObserver covers size changes;
          // creation/removal also invalidates positions for normal-flow slots.
          if (mutation.attributeName !== 'alt' && mutation.attributeName !== 'data-ai-disclosure-compact') {
            shouldReposition = true;
          }
          return;
        }

        if (mutation.type === 'attributes') {
          const target = mutation.target;

          if (
            target instanceof HTMLElement &&
            !target.hasAttribute(badgeAttribute) &&
            !target.matches('.media-disclosure')
          ) {
            if (mutation.attributeName === 'data-ai-disclosure-skip') processNode(target);
            shouldReposition = true;
          }

          return;
        }

        // Attribute writes were already filtered; ignore our child-list writes
        // too. A real media insertion/removal is still processed below.
        const changedNodes = [...mutation.addedNodes, ...mutation.removedNodes];
        if (changedNodes.length && changedNodes.every((node) =>
          node instanceof Element && (node.hasAttribute(badgeAttribute) || node === disclosurePopover)
        )) {
          // Recreate an externally removed badge; own removals already cleared
          // its entry or disconnected its image.
          changedNodes.forEach((node) => {
            const image = badgeImages.get(node);
            if (image?.isConnected && fallbackEntries.get(image)?.badge === node && !node.isConnected) {
              queueImage(image);
            }
          });
          return;
        }
        mutation.removedNodes.forEach(cleanupNode);
        mutation.addedNodes.forEach(processNode);
        shouldReposition = true;

        const serverDisclosureChanged =
          Array.from(mutation.addedNodes).some(containsServerDisclosureNode) ||
          Array.from(mutation.removedNodes).some(containsServerDisclosureNode);

        if (serverDisclosureChanged) processNearestImageScope(mutation.target);
      });

      if (shouldReposition) scheduleFallbackPositions();
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: [
        'alt',
        'data-ai-disclosure-compact',
        'data-ai-disclosure-skip',
        'class',
        'style',
        'hidden',
      ],
      childList: true,
      subtree: true,
    });

    document.addEventListener('load', (event) => {
      if (event.target instanceof HTMLImageElement) {
        queueImage(event.target);
        scheduleFallbackPositions();
      }
    }, true);
    document.addEventListener('click', handleDisclosureClick, true);
    document.addEventListener('keydown', handleDisclosureKeydown, true);
    window.addEventListener('resize', scheduleFallbackPositions, { passive: true });
    document.addEventListener('scroll', scheduleFallbackPositions, { capture: true, passive: true });
    scheduleFallbackPositions();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
